import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/core/add';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { messages } from '@databases/intl/messages';
import { type DatabaseEntry } from '@databases/types/database';
import React, { type FunctionComponent } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import useAddEntry from './use-add-entry';

const StyledButton = styled(Button)`
  border-radius: 0;

  && {
    align-items: center;
    height: 100%;
    text-align: left;
  }

  svg {
    height: 18px;
    width: auto;
  }
`;

const addEntryContainer = xcss({
  height: '40px',
  marginRight: 'space.050',
  marginBottom: 'space.0',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderBottomLeftRadius: 'radius.small',
  borderBottomRightRadius: 'radius.small',
  borderColor: 'color.border',
  backgroundColor: 'elevation.surface',
  zIndex: 'card',
});

const addEntryContainerFaded = xcss({
  height: '40px',
  marginRight: 'space.050',
  marginBottom: 'space.0',
  borderTopWidth: 'border.width',
  borderLeftWidth: 'border.width',
  borderRightWidth: 'border.width',
  borderBottomWidth: 'border.width.0',
  borderStyle: 'solid',
  borderBottomLeftRadius: 'radius.small',
  borderBottomRightRadius: 'radius.small',
  borderColor: 'color.border.disabled',
  backgroundColor: 'elevation.surface',
  zIndex: 'card',
});

const visualRefreshAddEntryContainerStyles = xcss({
  height: '40px',
  marginRight: 'space.050',
  marginBottom: 'space.0',
  borderBottomLeftRadius: 'radius.xlarge',
  borderBottomRightRadius: 'radius.xlarge',
  backgroundColor: 'elevation.surface',
  zIndex: 'card',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderColor: 'color.border',
});

const visualRefreshAddEntryContainerStylesFaded = xcss({
  height: '40px',
  marginRight: 'space.050',
  marginBottom: 'space.0',
  borderBottomLeftRadius: 'radius.small',
  borderBottomRightRadius: 'radius.small',
  backgroundColor: 'elevation.surface',
  zIndex: 'card',
  borderTop: 'none',
  borderBottom: 'none',
  background: `
    linear-gradient(to right, transparent 0%, ${token(
      'color.border.disabled'
    )} 3%, ${token('color.border.disabled')} 98%, transparent 100%) top
  `,
  backgroundSize: '100% 1px',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'top',
});

export interface AddEntryButtonProps {
  addEntryButtonRef: React.RefObject<HTMLButtonElement>;
  contentId: string;
  onAdd?: (entry: DatabaseEntry) => void;
  width: number | undefined;
  isFaded?: boolean;
}

const AddEntryButton: FunctionComponent<AddEntryButtonProps> = ({
  addEntryButtonRef,
  contentId,
  onAdd,
  width,
  isFaded = false,
}) => {
  const handleClick = useAddEntry(contentId, onAdd);
  const { formatMessage } = useIntl();

  const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
    'databases-frontend-visual-refresh-table-m1'
  );

  const stickyStyles = xcss({
    position: 'sticky',
    bottom: 'space.0',
    left: 'space.0',
    zIndex: 'blanket',
    backgroundColor: 'elevation.surface',
  });

  return (
    <Flex xcss={[stickyStyles, xcss({ width: '100%' })]}>
      <Box
        xcss={[
          isVisualRefreshM1Enabled
            ? isFaded
              ? visualRefreshAddEntryContainerStylesFaded
              : visualRefreshAddEntryContainerStyles
            : isFaded
            ? addEntryContainerFaded
            : addEntryContainer,
        ]}
        style={{
          // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
          marginRight: `0`,
          width: width ? width : '100%',
        }}
      >
        <StyledButton
          ref={addEntryButtonRef}
          appearance="subtle"
          onClick={handleClick}
          iconBefore={
            <AddIcon
              spacing="spacious"
              color={
                isFaded
                  ? token('color.text.disabled', '#8993A4')
                  : token('color.text.subtlest', '#626F86')
              }
              label=""
            />
          }
          spacing="compact"
          shouldFitContainer
          testId="table-layout-add-entry"
        >
          <Text
            color={isFaded ? 'color.text.disabled' : 'color.text.subtlest'}
            size={'medium'}
          >
            {formatMessage(messages['table-layout.add-entry'])}
          </Text>
        </StyledButton>
      </Box>
    </Flex>
  );
};

export default AddEntryButton;
